<template>
  <div class="retail-customer-display">
    <header class="customer-display-header">
      <div class="customer-display-brand">
        <div v-if="companyLogo" class="customer-display-logo">
          <img :src="companyLogo" :alt="companyName" />
        </div>
        <div v-else class="customer-display-brand-mark">P</div>
        <strong>{{ companyName }}</strong>
      </div>
      <div class="customer-display-header-meta">
        <span class="customer-display-counter">
          <i class="fas fa-store"></i>
          {{ $t('retailSale.customerDisplay.counter') }}
        </span>
        <span v-if="displayExchangeRate" class="customer-display-rate">
          <i class="fas fa-sync-alt"></i>
          {{ exchangeRateLabel }}
        </span>
      </div>
    </header>

    <div v-if="notification" class="customer-display-notification">
      <i class="fas fa-check-circle"></i>
      <span>{{ notification }}</span>
    </div>

    <main class="customer-display-main">
      <div class="customer-display-grid">
        <section class="customer-display-left-column">
          <div class="customer-display-section-heading">
            <div class="customer-display-heading-icon">
              <i class="fas fa-shopping-bag"></i>
            </div>
            <div>
              <div class="customer-display-heading-row">
                <h1>{{ $t('retailSale.customerDisplay.orderedItems') }}</h1>
                <span class="customer-display-count">
                  {{
                    $t('retailSale.customerDisplay.itemCount', {
                      count: itemCount
                    })
                  }}
                </span>
              </div>
              <p>{{ $t('retailSale.customerDisplay.liveBreakdown') }}</p>
            </div>
            <span v-if="items.length" class="customer-display-discount-note">
              <i class="fas fa-check-circle"></i>
              {{ $t('retailSale.customerDisplay.discountsApplied') }}
            </span>
            <span v-else class="customer-display-discount-note">
              <i class="fas fa-check-circle"></i>
              {{ $t('retailSale.customerDisplay.registerIdle') }}
            </span>
          </div>

          <div
            class="customer-display-item-card"
            :class="{ 'customer-display-item-card-empty': !items.length }"
          >
            <div v-if="items.length" class="customer-display-item-header">
              <span>{{ $t('retailSale.customerDisplay.itemDetails') }}</span>
              <span>{{ $t('retailSale.customerDisplay.qtyUnitPrice') }}</span>
              <span>{{ $t('retailSale.customerDisplay.totalAmount') }}</span>
            </div>
            <div v-if="!items.length" class="customer-display-empty">
              <div class="customer-display-empty-icon">
                <i class="fas fa-shopping-basket"></i>
              </div>
              <strong>{{ $t('retailSale.customerDisplay.noItemsScanned') }}</strong>
              <span>{{ $t('retailSale.customerDisplay.itemsAppearInRealTime') }}</span>
              <em>
                <i class="fas fa-circle"></i>
                {{ $t('retailSale.customerDisplay.waitingForNextItem') }}
              </em>
            </div>
            <div v-else class="customer-display-item-list">
              <div
                v-for="(item, index) in items"
                :key="item.cart_item_key || item.product_id + '-' + item.uom_id"
                class="customer-display-item"
              >
                <div class="customer-display-item-info">
                  <div class="customer-display-product-image">
                    <img
                      :src="itemImage(item)"
                      :alt="itemName(item)"
                      @error="handleItemImageError"
                    />
                    <span>{{ index + 1 }}</span>
                  </div>
                  <div class="customer-display-product-copy">
                    <div class="customer-display-product-title">
                      <strong>{{ itemName(item) }}</strong>
                      <span
                        v-if="item.is_free_promotion_item"
                        class="customer-display-promotion-badge customer-display-free-badge"
                      >
                        <i class="fas fa-check-circle"></i>
                        {{ $t('retailSale.customerDisplay.free') }}
                      </span>
                      <span
                        v-else-if="Number(item.discount_amount) > 0"
                        class="customer-display-promotion-badge customer-display-discount-badge"
                      >
                        <i class="fas fa-tag"></i>
                        {{
                          $t('retailSale.customerDisplay.discountOff', {
                            amount: formatAmount(item.discount_amount)
                          })
                        }}
                      </span>
                    </div>
                    <small v-if="item.remarks">{{ item.remarks }}</small>
                    <small v-else>{{ itemUomName(item) }}</small>
                  </div>
                </div>
                <div class="customer-display-item-quantity">
                  <span
                    >{{ $t('retailSale.customerDisplay.qty') }}:
                    {{ item.quantity }}</span
                  >
                  <em>× {{ formatAmount(item.unit_price) }}</em>
                </div>
                <div class="customer-display-item-total">
                  <strong
                    v-if="item.is_free_promotion_item"
                    class="customer-display-free-total"
                  >
                    {{ $t('retailSale.customerDisplay.free') }}
                  </strong>
                  <strong
                    v-else
                    :class="{
                      'customer-display-discount-total':
                        Number(item.discount_amount) > 0
                    }"
                  >
                    {{ formatAmount(item.grand_total) }}
                  </strong>
                  <small v-if="item.is_free_promotion_item">
                    {{
                      $t('retailSale.customerDisplay.saved', {
                        amount: formatAmount(
                          Number(item.unit_price || 0) *
                            Number(item.quantity || 0)
                        )
                      })
                    }}
                  </small>
                  <small v-else-if="displayExchangeRate">
                    {{ formatConvertedAmount(item.grand_total) }}
                    {{ convertedCurrencyCode }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </section>

        <aside class="customer-display-right-column">
          <section class="customer-display-payable-card">
            <div class="customer-display-payable-top">
              <div>
                <span>{{ $t('retailSale.customerDisplay.amountToPay') }}</span>
                <small>{{
                  $t('retailSale.customerDisplay.amountToPayKh')
                }}</small>
              </div>
              <span class="customer-display-payment-status">
                <i></i>
                {{
                  $t(
                    items.length
                      ? 'retailSale.customerDisplay.awaitingPayment'
                      : 'retailSale.customerDisplay.laneReady'
                  )
                }}
              </span>
            </div>
            <div class="customer-display-payable-amount">
              <span>{{ $t('retailSale.customerDisplay.totalPayable') }}</span>
              <strong>
                {{ formatAmount(order.grand_total) }}
                <small>{{ currency.currency_code }}</small>
              </strong>
            </div>
            <div v-if="displayExchangeRate" class="customer-display-equivalent">
              <div>
                <span>{{
                  $t(
                    items.length
                      ? 'retailSale.customerDisplay.equivalent'
                      : 'retailSale.customerDisplay.khmerRielEquivalent'
                  )
                }}</span>
                <small>{{ exchangeRateLabel }}</small>
              </div>
              <strong>
                {{ formatConvertedAmount(order.grand_total) }}
                <small>{{ convertedCurrencyCode }}</small>
              </strong>
            </div>
          </section>

          <section class="customer-display-summary-card">
            <div class="customer-display-card-heading">
              <h2>
                <i class="fas fa-receipt"></i
                >{{ $t('retailSale.customerDisplay.billSummary') }}
              </h2>
              <span>{{
                $t('retailSale.customerDisplay.itemsTotal', {
                  count: itemCount
                })
              }}</span>
            </div>
            <div class="customer-display-summary-row">
              <span>{{ $t('retailSale.customerDisplay.subtotal') }}</span>
              <div class="customer-display-summary-value">
                <strong>{{ formatAmount(order.sub_total) }}</strong>
                <small v-if="displayExchangeRate">
                  {{ formatConvertedAmount(order.sub_total) }}
                  {{ convertedCurrencyCode }}
                </small>
              </div>
            </div>
            <div class="customer-display-summary-row customer-display-savings">
              <div>
                <span
                  ><i class="fas fa-tag"></i
                  >{{
                    $t(
                      items.length
                        ? 'retailSale.customerDisplay.totalSavings'
                        : 'retailSale.customerDisplay.noDiscountsApplied'
                    )
                  }}</span
                >
                <small v-if="!items.length">
                  {{ $t('retailSale.customerDisplay.noDiscountsApplied') }}
                </small>
              </div>
              <div class="customer-display-summary-value">
                <strong
                  >{{ discountAmount ? '-' : '' }}{{
                    formatAmount(discountAmount)
                  }}</strong
                >
                <small v-if="displayExchangeRate">
                  {{ $t('retailSale.customerDisplay.saved', {
                    amount: formatConvertedAmount(discountAmount)
                  }) }}
                  {{ convertedCurrencyCode }}
                </small>
              </div>
            </div>
            <div class="customer-display-summary-row">
              <span>
                {{
                  $t('retailSale.customerDisplay.vatTax', {
                    rate: vatRateLabel
                  })
                }}
              </span>
              <div class="customer-display-summary-value">
                <strong>+{{ formatAmount(vatAmount) }}</strong>
                <small v-if="displayExchangeRate">
                  {{ formatConvertedAmount(vatAmount) }}
                  {{ convertedCurrencyCode }}
                </small>
              </div>
            </div>
          </section>

          <section class="customer-display-payment-card">
            <div class="customer-display-card-heading">
              <h2>
                <i class="fas fa-money-bill-wave"></i
                >{{ $t('retailSale.customerDisplay.acceptedPaymentMethods') }}
              </h2>
              <span class="customer-display-instant">
                <i></i>{{ $t('retailSale.customerDisplay.instantCheckout') }}
              </span>
            </div>
            <div class="customer-display-payment-groups">
              <div class="customer-display-payment-group">
                <div class="customer-display-payment-group-heading">
                  <span>
                    <i class="fas fa-university"></i>
                    {{ $t('retailSale.customerDisplay.bankTransfer') }}
                  </span>
                </div>
                <div class="customer-display-payment-detail">
                  <i class="fas fa-qrcode customer-display-payment-detail-icon"></i>
                  <div>
                    <strong>{{ $t('retailSale.customerDisplay.khqrMobileBanking') }}</strong>
                    <small>{{ $t('retailSale.customerDisplay.scanQrOrTransfer') }}</small>
                  </div>
                </div>
              </div>
              <div class="customer-display-payment-group">
                <div class="customer-display-payment-group-heading">
                  <span>
                    <i class="fas fa-money-bill-wave"></i>
                    {{ $t('retailSale.customerDisplay.cashPayment') }}
                  </span>
                  <b>{{ $t('retailSale.customerDisplay.changeGiven') }}</b>
                </div>
                <div class="customer-display-payment-detail">
                  <i class="fas fa-coins customer-display-payment-detail-icon"></i>
                  <div>
                    <strong>{{ $t('retailSale.customerDisplay.usdKhrCash') }}</strong>
                    <small>{{ $t('retailSale.customerDisplay.dualCurrencyAccepted') }}</small>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </main>
  </div>
</template>

<script>
import { formatCurrency, formatExchangeRate } from '../utils/currency'
import {
  loadRetailSaleDrafts,
  createRetailSaleBroadcastChannel
} from '../utils/retail-sale'
import retailSaleDataService from '../services/retail-sale-data.service'

export default {
  name: 'RetailSaleCustomerDisplay',
  data() {
    return {
      order: { id: null, cartItems: [] },
      currency: {},
      exchangeRates: [],
      storageHandler: null,
      broadcastChannel: null,
      notification: null,
      notificationTimer: null
    }
  },
  computed: {
    items() {
      return this.order.cartItems || []
    },
    itemCount() {
      return this.items.reduce(
        (total, item) => total + (Number(item.quantity) || 0),
        0
      )
    },
    company() {
      const authUser = this.$store.state.authUser || {}
      const user = authUser.user || {}
      const defaultBranch = user.default_branch || {}

      return defaultBranch.company || {}
    },
    companyLogo() {
      return this.company.company_logo || ''
    },
    companyName() {
      const locale = this.$root.$i18n ? this.$root.$i18n.locale : 'en'

      return (
        (locale === 'kh'
          ? this.company.company_name_kh
          : this.company.company_name_en) ||
        this.company.company_name_en ||
        this.company.company_name_kh ||
        'POS-CLOUD'
      )
    },
    discountAmount() {
      return Number(
        this.order.discount || this.order.invoice_discount_amount || 0
      )
    },
    vatAmount() {
      const storedVatAmount = Number(this.order.vat_amount)
      if (Number.isFinite(storedVatAmount) && storedVatAmount !== 0) {
        return storedVatAmount
      }

      const totalAfterDiscount = Number(this.order.total_after_discount)
      const vatRate = Number(this.order.vat_rate)
      if (
        Number.isFinite(totalAfterDiscount) &&
        Number.isFinite(vatRate) &&
        vatRate > 0
      ) {
        return totalAfterDiscount * (vatRate / 100)
      }

      const grandTotal = Number(this.order.grand_total)
      if (Number.isFinite(grandTotal) && Number.isFinite(totalAfterDiscount)) {
        return Math.max(grandTotal - totalAfterDiscount, 0)
      }

      return 0
    },
    vatRateLabel() {
      const rate = Number(this.order.vat_rate)

      return Number.isFinite(rate) ? rate : 0
    },
    displayExchangeRate() {
      return this.exchangeRates.find(
        rate => Number(rate.fx_rate) > 0 && Number(rate.fx_rate_ratio) > 0
      )
    },
    convertedCurrencyCode() {
      return this.displayExchangeRate
        ? this.displayExchangeRate.to_currency_code || ''
        : ''
    },
    exchangeRateLabel() {
      const rate = this.displayExchangeRate

      return rate
        ? '1 ' +
            (this.currency.currency_code || '') +
            ' = ' +
            formatExchangeRate(rate.fx_rate) +
            ' ' +
            (rate.to_currency_code || '')
        : ''
    }
  },
  async created() {
    this.updateDisplayOrder()
    this.storageHandler = event => {
      if (event.key === 'retail-sale:drafts:v1') this.updateDisplayOrder()
      if (event.key === 'langCode') this.applyLanguage(event.newValue)
    }
    window.addEventListener('storage', this.storageHandler)
    this.broadcastChannel = createRetailSaleBroadcastChannel()
    if (this.broadcastChannel) {
      this.broadcastChannel.onmessage = event => {
        const message = event && event.data ? event.data : {}
        if (message.type === 'payment-success') {
          if (message.order) this.order = message.order
          this.showNotification(
            this.$t('retailSale.customerDisplay.paymentReceived')
          )
          return
        }

        if (message.type === 'order-updated') this.updateDisplayOrder()
        if (message.type === 'language-changed') this.applyLanguage(message.locale)
      }
    }

    const results = await Promise.all([
      retailSaleDataService.fetchCurrency().catch(() => ({ currency: {} })),
      retailSaleDataService.fetchExchangeRates().catch(() => [])
    ])

    this.currency = results[0].currency || {}
    this.exchangeRates = results[1] || []
  },
  beforeDestroy() {
    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler)
    }
    if (this.broadcastChannel) this.broadcastChannel.close()
    if (this.notificationTimer) clearTimeout(this.notificationTimer)
  },
  methods: {
    updateDisplayOrder() {
      const saved = loadRetailSaleDrafts()
      this.order = saved.orders.find(
        order => order.id === saved.activeOrderId
      ) ||
        saved.orders[0] || { id: null, cartItems: [] }
    },
    showNotification(message) {
      this.notification = message
      if (this.notificationTimer) clearTimeout(this.notificationTimer)
      this.notificationTimer = setTimeout(() => {
        this.notification = null
        this.notificationTimer = null
        this.updateDisplayOrder()
      }, 4000)
    },
    applyLanguage(locale) {
      if (!['en', 'kh'].includes(locale) || !this.$root.$i18n) return

      this.$root.$i18n.locale = locale
      document.documentElement.setAttribute('lang', locale)
    },
    itemName(item) {
      const isKhmer = this.$root.$i18n && this.$root.$i18n.locale === 'kh'

      return isKhmer
        ? item.nameKh || item.name || ''
        : item.name || item.nameKh || ''
    },
    itemUomName(item) {
      const isKhmer = this.$root.$i18n && this.$root.$i18n.locale === 'kh'

      return isKhmer
        ? item.uom_name_kh || item.uom_name_en || ''
        : item.uom_name_en || item.uom_name_kh || ''
    },
    itemImage(item) {
      if (item.image) return item.image

      const product = (this.order.products || []).find(
        value => value.id === item.product_id
      )

      return product && product.image
        ? product.image
        : require('@/assets/default-retail-product.webp')
    },
    handleItemImageError(event) {
      event.target.onerror = null
      event.target.src = require('@/assets/default-retail-product.webp')
    },
    formatAmount(amount) {
      return formatCurrency(amount, this.currency)
    },
    formatConvertedAmount(amount) {
      const rate = this.displayExchangeRate
      if (!rate) return ''

      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0
      }).format((Number(amount) || 0) * Number(rate.fx_rate))
    }
  }
}
</script>

<style scoped src="../styles/customer-display.css"></style>
